import React, { useState, useMemo } from "react";
import useRealtimeDraft from '../../../realtime/useRealtimeDraft';
import { useSelector, useDispatch } from "react-redux";
import { selectUserDetails } from "../../../redux/slices/userSlice";
import { selectJobChangeOrderDetails, selectJobId, selectChangeOrderParent, selectGeneralProject } from "../../../redux/selectors";
import { Container, Row, Col, Form } from 'react-bootstrap';
import { patchData } from '../../../redux/slices/apiCallSlice';
import { formatDateForDisplay } from "../../../utils/dateUtils";

const EditChangeOrder = ({ setNotes, notes, impactsCSM, setImpactsCSM, includeAllControls, setIncludeAllControls }) => {
        useRealtimeDraft(true); // The preview/editor is a stable draft until closed.
        const jobId = useSelector(selectJobId);
        const userDetails = useSelector(selectUserDetails);
        const currentUser = userDetails?.name;
        const projectDetails = useSelector(selectGeneralProject);
        const changeOrderDetails = useSelector(selectJobChangeOrderDetails); // Use job-specific selector from fullJobFetchSlice
        const changeOrderParent = useSelector(selectChangeOrderParent);
        const dispatch = useDispatch();

        const tbdList = useSelector((state) => state.tbdApi.tbdList);
        const filteredTbdList = useMemo(() => {
            return tbdList.filter(
              (tbdItem) => String(tbdItem.ProjectID) === String(jobId)
            );
        }, [tbdList, jobId]);

    const getFirstDefinedValue = (detail, keys) => {
        for (const key of keys) {
            const value = detail?.[key];
            if (value !== undefined && value !== null) {
                return value;
            }
        }
        return undefined;
    };

    const toDisplayValue = (value, fallback = "BLANK") => {
        return value === undefined || value === null || value === "" ? fallback : value;
    };

    const toCamelCaseKey = (key) => key.charAt(0).toLowerCase() + key.slice(1);
    const toSnakeCaseKey = (key) => key.replace(/[A-Z]/g, (letter, index) => `${index > 0 ? "_" : ""}${letter.toLowerCase()}`);

    const getFieldValue = (detail, key) => {
        return getFirstDefinedValue(detail, [key, toCamelCaseKey(key), toSnakeCaseKey(key)]);
    };

    const normalizeLabelPart = (value) => {
        if (value === undefined || value === null) {
            return "";
        }

        const trimmedValue = String(value).trim();
        if (trimmedValue === "" || trimmedValue.toUpperCase() === "BLANK") {
            return "";
        }

        const withoutTrailingPlaceholder = trimmedValue.replace(/\s+BLANK$/i, "").trim();
        return withoutTrailingPlaceholder.toUpperCase() === "BLANK" ? "" : withoutTrailingPlaceholder;
    };

    const getDetailTitle = (detail) => normalizeLabelPart(getFirstDefinedValue(detail, ["Title", "title"]));
    const getDetailField = (detail) => normalizeLabelPart(getFirstDefinedValue(detail, ["Field", "field", "FieldName", "fieldName", "field_name"]));
    const getDetailExistingValue = (detail) => toDisplayValue(getFirstDefinedValue(detail, ["ExistingValue", "existingValue", "PreviousValue", "previousValue", "PrevValue", "prevValue", "existing_value", "previous_value"]));
    const getDetailNewValue = (detail) => toDisplayValue(getFirstDefinedValue(detail, ["NewValue", "newValue", "UpdatedValue", "updatedValue", "new_value", "updated_value"]));
    const getChangeLabel = (detail) => {
        const labelParts = [getDetailTitle(detail), getDetailField(detail)].filter(Boolean);
        return labelParts.length > 0 ? labelParts.join(" ") : "BLANK";
    };

    const getStatus = (detail) => getFirstDefinedValue(detail, ["Status", "status"]);
    const getBillCreditTo = (detail) => getFirstDefinedValue(detail, ["BillCreditTo", "billCreditTo", "bill_credit_to"]);
    const getChangeOrderNumber = (detail) => getFirstDefinedValue(detail, ["ChangeOrderNumber", "changeOrderNumber", "change_order_number"]);
    const hasChangeOrderNumber = (detail) => {
        const value = getChangeOrderNumber(detail);
        return value !== undefined && value !== null && value !== "";
    };

    const countChangeOrderParent = () => {
        return changeOrderParent ? changeOrderParent.length : 0;
    };

    // Filter items that do NOT have a change order number key/value (pending items from fullJobFetch API)
    const includeOnChangeOrder = changeOrderDetails.filter(detail => !hasChangeOrderNumber(detail));

    // noBill includes items with no billing info OR undefined BillCreditTo (from fullJobFetch API)
    const noBill = includeOnChangeOrder.filter(detail => {
        const billCreditTo = getBillCreditTo(detail);
        const status = getStatus(detail);
        return (billCreditTo === null || billCreditTo === "N/A" || billCreditTo === "" || billCreditTo === "BLANK" || billCreditTo === undefined) && status !== "Follow Up";
    });
    const watertronicsBill = includeOnChangeOrder.filter(detail => getBillCreditTo(detail) === "Watertronics" && getStatus(detail) !== "Follow Up");
    const customerBill = includeOnChangeOrder.filter(detail => getBillCreditTo(detail) === "Customer" && getStatus(detail) !== "Follow Up");
    const followUp = includeOnChangeOrder.filter(detail => getStatus(detail) === "Follow Up");

        const currentDate = formatDateForDisplay(new Date());
        const [localChangeOrderDetails, setLocalChangeOrderDetails] = useState(changeOrderDetails);

        // Memoize the local bill categorizations to recalculate when localChangeOrderDetails changes
        const localIncludeOnChangeOrder = useMemo(() =>
            localChangeOrderDetails.filter(detail => !hasChangeOrderNumber(detail)),
            [localChangeOrderDetails, hasChangeOrderNumber]
        );

        const localWatertronicsBill = useMemo(() =>
            localIncludeOnChangeOrder.filter(detail => getBillCreditTo(detail) === "Watertronics" && getStatus(detail) !== "Follow Up"),
            [localIncludeOnChangeOrder, getBillCreditTo, getStatus]
        );

        const localCustomerBill = useMemo(() =>
            localIncludeOnChangeOrder.filter(detail => getBillCreditTo(detail) === "Customer" && getStatus(detail) !== "Follow Up"),
            [localIncludeOnChangeOrder, getBillCreditTo, getStatus]
        );

        const handleInputChange = (detailId, field, value) => {
            setLocalChangeOrderDetails(prevDetails =>
                prevDetails.map(detail =>
                    detail.ID === detailId ? { ...detail, [field]: value === "" ? "BLANK" : value } : detail
                )
            );
        };

        const handleInputBlur = (detailId, field, value) => {
            const originalDetail = changeOrderDetails.find(detail => detail.ID === detailId);
            const valueToSave = value === "" ? "BLANK" : value;

            if (originalDetail[field] !== valueToSave) {
                const url = `${process.env.REACT_APP_API_URL}/change-order-details`;
                const data = { [field]: valueToSave, ID: detailId };

                dispatch(patchData({ url, data }));
            }
        };

        const handleToggleChange = (detailId, field, value) => {
            const booleanValue = value;
            const stringValue = value ? "true" : "false";
            setLocalChangeOrderDetails(prevDetails =>
                prevDetails.map(detail =>
                    detail.ID === detailId ? { ...detail, [field]: booleanValue } : detail
                )
            );
            handleInputBlur(detailId, field, stringValue);

            const url = `${process.env.REACT_APP_API_URL}/change-order-details`;
            const data = { [field]: stringValue, ID: detailId };

            dispatch(patchData({ url, data }));
        };

        // Helper function to calculate total cost/credit for an item
        const calculateTotalImpact = (detail) => {
            const manufacturingHours = parseFloat(detail.ManufacturingLaborHours) || 0;
            const engineeringHours = parseFloat(detail.EngineeringLaborHours) || 0;
            const materialsVariance = parseFloat(detail.MaterialsVariance) || 0;
            const scrap = parseFloat(detail.Scrap) || 0;

            // Labor rates should match the summary section
            const manufacturingRate = 90; // $/hour
            const engineeringRate = 165; // $/hour

            let total = 0;
            total += (detail.ManufacturingCostCredit === true || detail.ManufacturingCostCredit === "true" ? -1 : 1) * manufacturingHours * manufacturingRate;
            total += (detail.EngineeringCostCredit === true || detail.EngineeringCostCredit === "true" ? -1 : 1) * engineeringHours * engineeringRate;
            total += (detail.MaterialCostCredit === true || detail.MaterialCostCredit === "true" ? -1 : 1) * materialsVariance;
            total += (detail.ScrapCostCredit === true || detail.ScrapCostCredit === "true" ? -1 : 1) * scrap;

            return total;
        };

        // Helper function to format the cost impact message
        const formatCostImpactMessage = (detail, billTo) => {
            if (!detail) return null;
            const total = calculateTotalImpact(detail);
            if (total === 0) return null;

            const absoluteTotal = Math.abs(total).toFixed(2);
            if (total > 0) {
                return `This change will cost ${billTo} $${absoluteTotal}`;
            } else {
                return `${billTo} will be credited $${absoluteTotal} for this change`;
            }
        };

        // Helper function to calculate totals for cost summary
        const calculateTotal = (details, field, costCreditField) => {
            return details.reduce((acc, detail) => {
                const fieldValue = parseFloat(getFieldValue(detail, field)) || 0;
                const costCreditValue = getFieldValue(detail, costCreditField);

                // If costCreditField is undefined (missing from API), default to false (cost)
                // costCreditValue === true means credit (positive), anything else means cost (negative)
                const multiplier = (costCreditValue === true || costCreditValue === "true") ? 1 : -1;
                const value = fieldValue * multiplier;

                return acc + value;
            }, 0);
        };

        // Calculate customer billing totals
        const customerManufacturingLaborTotal = calculateTotal(localCustomerBill, 'ManufacturingLaborHours', 'ManufacturingCostCredit') * 90;
        const customerEngineeringLaborTotal = calculateTotal(localCustomerBill, 'EngineeringLaborHours', 'EngineeringCostCredit') * 165;
        const customerMaterialsVarianceTotal = calculateTotal(localCustomerBill, 'MaterialsVariance', 'MaterialCostCredit');
        const customerScrapTotal = calculateTotal(localCustomerBill, 'Scrap', 'ScrapCostCredit');

        // Calculate watertronics billing totals
        const watertronicsManufacturingLaborTotal = calculateTotal(localWatertronicsBill, 'ManufacturingLaborHours', 'ManufacturingCostCredit') * 90;
        const watertronicsEngineeringLaborTotal = calculateTotal(localWatertronicsBill, 'EngineeringLaborHours', 'EngineeringCostCredit') * 165;
        const watertronicsMaterialsVarianceTotal = calculateTotal(localWatertronicsBill, 'MaterialsVariance', 'MaterialCostCredit');
        const watertronicsScrapTotal = calculateTotal(localWatertronicsBill, 'Scrap', 'ScrapCostCredit');

        // Calculate grand totals
        const totalManufacturingLabor = customerManufacturingLaborTotal + watertronicsManufacturingLaborTotal;
        const totalEngineeringLabor = customerEngineeringLaborTotal + watertronicsEngineeringLaborTotal;
        const totalMaterialsVariance = customerMaterialsVarianceTotal + watertronicsMaterialsVarianceTotal;
        const totalScrap = customerScrapTotal + watertronicsScrapTotal;

        const customerBillTotal = customerManufacturingLaborTotal + customerEngineeringLaborTotal + customerMaterialsVarianceTotal + customerScrapTotal;
        const watertronicsBillTotal = watertronicsManufacturingLaborTotal + watertronicsEngineeringLaborTotal + watertronicsMaterialsVarianceTotal + watertronicsScrapTotal;

        const formatCostCredit = (value) => {
            return value < 0 ? `${Math.abs(value).toFixed(2)} Cost` : `${value.toFixed(2)} Credit`;
        };


        const handleNotesChange = (e) => {
            const value = e.target.value || "BLANK";
            setNotes(value);
        };

    // Check if data is loaded
    if (!changeOrderDetails || changeOrderDetails.length === 0) {
        return <div>Loading change order details...</div>;
    }

    return (
        <Container className="change-order-document change-order-edit">
            {projectDetails ? (
                <>
                    <Form className="change-order-edit-overview">
                        <Row className="change-order-edit-overview-grid">
                            <Col>
                                <Form.Group controlId="projectName">
                                    <Form.Label><strong>Project Name:</strong></Form.Label>
                                    <span> {projectDetails[0].ProjectName || "BLANK"}</span>
                                </Form.Group>
                                <Form.Group controlId="jobNumber">
                                    <Form.Label><strong>Job Number:</strong></Form.Label>
                                    <span> {projectDetails[0].JobNumber || "BLANK"}</span>
                                </Form.Group>
                                <Form.Group controlId="changeOrderNumber">
                                    <Form.Label><strong>Change Order #:</strong></Form.Label>
                                    <span> {countChangeOrderParent() + 1}</span>
                                </Form.Group>
                                <Form.Group controlId="dateSubmitted">
                                    <Form.Label><strong>Date Submitted:</strong></Form.Label>
                                    <span> {currentDate}</span>
                                </Form.Group>
                                <Form.Group controlId="writtenBy">
                                    <Form.Label><strong>Written By:</strong></Form.Label>
                                    <span> {currentUser || "BLANK"}</span>
                                </Form.Group>
                                <Form.Group controlId="impactsCSM" className="d-flex align-items-center">
                                    <Form.Label className="me-2"><strong>Impacts CSM:</strong></Form.Label>
                                    <Form.Check
                                        type="switch"
                                        checked={impactsCSM}
                                        onChange={(e) => setImpactsCSM(e.target.checked)}
                                    />
                                </Form.Group>
                            </Col>
                            <Col>
                                <Form.Group controlId="salesRep">
                                    <Form.Label><strong>Sales Rep:</strong></Form.Label>
                                    <span> {projectDetails[0].SalesRep || "BLANK"}</span>
                                </Form.Group>
                                <Form.Group controlId="projectManager">
                                    <Form.Label><strong>Project Manager:</strong></Form.Label>
                                    <span> {projectDetails[0].ProjectManager || "BLANK"}</span>
                                </Form.Group>
                                <Form.Group controlId="mechanicalEngineer">
                                    <Form.Label><strong>Mechanical Engineer:</strong></Form.Label>
                                    <span> {projectDetails[0].MechanicalEngineer || "BLANK"}</span>
                                </Form.Group>
                                <Form.Group controlId="electricalEngineer">
                                    <Form.Label><strong>Electrical Engineer:</strong></Form.Label>
                                    <span> {projectDetails[0].ElectricalEngineer || "BLANK"}</span>
                                </Form.Group>
                                <Form.Group controlId="controlsEngineer">
                                    <Form.Label><strong>Controls Engineer:</strong></Form.Label>
                                    <span> {projectDetails[0].ControlsEngineer || "BLANK"}</span>
                                </Form.Group>
                                <Form.Group controlId="includeAllControls" className="d-flex align-items-center">
                                    <Form.Label className="me-2"><strong>Include all Controls?</strong></Form.Label>
                                    <Form.Check
                                        type="switch"
                                        checked={includeAllControls}
                                        onChange={(e) => setIncludeAllControls(e.target.checked)}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                    </Form>
                    {noBill.length > 0 && (
                        <div>
                            <h2 className="mt-3">Changes</h2>
                            <div className="border p-1 mb-3">
                                {noBill.map((detail, index) => (
                                    <div key={index} className="border p-2 mb-2">
                                        <p className="mt-2 mb-2">{getChangeLabel(detail)} changed from {getDetailExistingValue(detail)} to {getDetailNewValue(detail)}</p>

                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    {customerBill.length > 0 && (
                        <div>
                            <h2 className="mt-3">Changes Billed to Customer</h2>
                            <div className="border p-1 mb-3">
                                {customerBill.map((detail, index) => (
                                    <div key={index} className="border p-2 mb-3">
                                        <p className="mt-2 mb-2">{getChangeLabel(detail)} changed from {getDetailExistingValue(detail)} to {getDetailNewValue(detail)}</p>
                                        <Form>
                                            <Row>
                                                <Col>
                                                    <Form.Group controlId={`manufacturingLaborHours-${index}`}>
                                                        <Form.Label>Manufacturing Labor Hours</Form.Label>
                                                        <Form.Control
                                                            type="number"
                                                            value={localChangeOrderDetails.find(d => d.ID === detail.ID)?.ManufacturingLaborHours || ''}
                                                            onChange={(e) => handleInputChange(detail.ID, 'ManufacturingLaborHours', e.target.value)}
                                                            onBlur={(e) => handleInputBlur(detail.ID, 'ManufacturingLaborHours', e.target.value)}
                                                        />
                                                        <div className="d-flex align-items-center mt-2">
                                                            <Form.Check
                                                                type="switch"
                                                                checked={localChangeOrderDetails.find(d => d.ID === detail.ID)?.ManufacturingCostCredit === true || localChangeOrderDetails.find(d => d.ID === detail.ID)?.ManufacturingCostCredit === "true"}
                                                                onChange={(e) => handleToggleChange(detail.ID, 'ManufacturingCostCredit', e.target.checked)}
                                                                className="me-2"
                                                            />
                                                            <Form.Label className="mb-0">
                                                                {(localChangeOrderDetails.find(d => d.ID === detail.ID)?.ManufacturingCostCredit === true || localChangeOrderDetails.find(d => d.ID === detail.ID)?.ManufacturingCostCredit === "true") ? 'Credit' : 'Cost'}
                                                            </Form.Label>
                                                        </div>
                                                    </Form.Group>
                                                </Col>
                                                <Col>
                                                    <Form.Group controlId={`engineeringLaborHours-${index}`}>
                                                        <Form.Label>Engineering Labor Hours</Form.Label>
                                                        <Form.Control
                                                            type="number"
                                                            value={localChangeOrderDetails.find(d => d.ID === detail.ID)?.EngineeringLaborHours || ''}
                                                            onChange={(e) => handleInputChange(detail.ID, 'EngineeringLaborHours', e.target.value)}
                                                            onBlur={(e) => handleInputBlur(detail.ID, 'EngineeringLaborHours', e.target.value)}
                                                        />
                                                        <div className="d-flex align-items-center mt-2">
                                                            <Form.Check
                                                                type="switch"
                                                                checked={localChangeOrderDetails.find(d => d.ID === detail.ID)?.EngineeringCostCredit === true || localChangeOrderDetails.find(d => d.ID === detail.ID)?.EngineeringCostCredit === "true"}
                                                                onChange={(e) => handleToggleChange(detail.ID, 'EngineeringCostCredit', e.target.checked)}
                                                                className="me-2"
                                                            />
                                                            <Form.Label className="mb-0">
                                                                {(localChangeOrderDetails.find(d => d.ID === detail.ID)?.EngineeringCostCredit === true || localChangeOrderDetails.find(d => d.ID === detail.ID)?.EngineeringCostCredit === "true") ? 'Credit' : 'Cost'}
                                                            </Form.Label>
                                                        </div>
                                                    </Form.Group>
                                                </Col>
                                            </Row>
                                            <Row>
                                                <Col>
                                                    <Form.Group controlId={`materialsVariance-${index}`}>
                                                        <Form.Label>Materials Variance ($)</Form.Label>
                                                        <Form.Control
                                                            type="number"
                                                            value={localChangeOrderDetails.find(d => d.ID === detail.ID)?.MaterialsVariance || ''}
                                                            onChange={(e) => handleInputChange(detail.ID, 'MaterialsVariance', e.target.value)}
                                                            onBlur={(e) => handleInputBlur(detail.ID, 'MaterialsVariance', e.target.value)}
                                                        />
                                                        <div className="d-flex align-items-center mt-2">
                                                            <Form.Check
                                                                type="switch"
                                                                checked={localChangeOrderDetails.find(d => d.ID === detail.ID)?.MaterialCostCredit === true || localChangeOrderDetails.find(d => d.ID === detail.ID)?.MaterialCostCredit === "true"}
                                                                onChange={(e) => handleToggleChange(detail.ID, 'MaterialCostCredit', e.target.checked)}
                                                                className="me-2"
                                                            />
                                                            <Form.Label className="mb-0">
                                                                {(localChangeOrderDetails.find(d => d.ID === detail.ID)?.MaterialCostCredit === true || localChangeOrderDetails.find(d => d.ID === detail.ID)?.MaterialCostCredit === "true") ? 'Credit' : 'Cost'}
                                                            </Form.Label>
                                                        </div>
                                                    </Form.Group>
                                                </Col>
                                                <Col>
                                                    <Form.Group controlId={`scrap-${index}`}>
                                                        <Form.Label>Scrap ($)</Form.Label>
                                                        <Form.Control
                                                            type="number"
                                                            value={localChangeOrderDetails.find(d => d.ID === detail.ID)?.Scrap || ''}
                                                            onChange={(e) => handleInputChange(detail.ID, 'Scrap', e.target.value)}
                                                            onBlur={(e) => handleInputBlur(detail.ID, 'Scrap', e.target.value)}
                                                        />
                                                        <div className="d-flex align-items-center mt-2">
                                                            <Form.Check
                                                                type="switch"
                                                                checked={localChangeOrderDetails.find(d => d.ID === detail.ID)?.ScrapCostCredit === true || localChangeOrderDetails.find(d => d.ID === detail.ID)?.ScrapCostCredit === "true"}
                                                                onChange={(e) => handleToggleChange(detail.ID, 'ScrapCostCredit', e.target.checked)}
                                                                className="me-2"
                                                            />
                                                            <Form.Label className="mb-0">
                                                                {(localChangeOrderDetails.find(d => d.ID === detail.ID)?.ScrapCostCredit === true || localChangeOrderDetails.find(d => d.ID === detail.ID)?.ScrapCostCredit === "true") ? 'Credit' : 'Cost'}
                                                            </Form.Label>
                                                        </div>
                                                    </Form.Group>
                                                </Col>
                                            </Row>
                                        </Form>
                                        {formatCostImpactMessage(localChangeOrderDetails.find(d => d.ID === detail.ID), "Customer") && (
                                            <div className="mt-3 p-2" style={{backgroundColor: '#f8f9fa', borderLeft: '4px solid #0d6efd'}}>
                                                <strong>{formatCostImpactMessage(localChangeOrderDetails.find(d => d.ID === detail.ID), "Customer")}</strong>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    {watertronicsBill.length > 0 && (
                        <div>
                            <h2 className="mt-3">Changes Billed to Watertronics</h2>
                            <div className="border p-1 mb-3">
                                {watertronicsBill.map((detail, index) => (
                                    <div key={index} className="border p-2 mb-3">
                                        <p className="mt-2 mb-2">{getChangeLabel(detail)} changed from {getDetailExistingValue(detail)} to {getDetailNewValue(detail)}</p>

                                        <Form>
                                            <Row>
                                                <Col>
                                                    <Form.Group controlId={`manufacturingLaborHours-w-${index}`}>
                                                        <Form.Label>Manufacturing Labor Hours</Form.Label>
                                                        <Form.Control
                                                            type="number"
                                                            value={localChangeOrderDetails.find(d => d.ID === detail.ID)?.ManufacturingLaborHours || ''}
                                                            onChange={(e) => handleInputChange(detail.ID, 'ManufacturingLaborHours', e.target.value)}
                                                            onBlur={(e) => handleInputBlur(detail.ID, 'ManufacturingLaborHours', e.target.value)}
                                                        />
                                                        <div className="d-flex align-items-center mt-2">
                                                            <Form.Check
                                                                type="switch"
                                                                checked={localChangeOrderDetails.find(d => d.ID === detail.ID)?.ManufacturingCostCredit === true || localChangeOrderDetails.find(d => d.ID === detail.ID)?.ManufacturingCostCredit === "true"}
                                                                onChange={(e) => handleToggleChange(detail.ID, 'ManufacturingCostCredit', e.target.checked)}
                                                                className="me-2"
                                                            />
                                                            <Form.Label className="mb-0">
                                                                {(localChangeOrderDetails.find(d => d.ID === detail.ID)?.ManufacturingCostCredit === true || localChangeOrderDetails.find(d => d.ID === detail.ID)?.ManufacturingCostCredit === "true") ? 'Credit' : 'Cost'}
                                                            </Form.Label>
                                                        </div>
                                                    </Form.Group>
                                                </Col>
                                                <Col>
                                                    <Form.Group controlId={`engineeringLaborHours-w-${index}`}>
                                                        <Form.Label>Engineering Labor Hours</Form.Label>
                                                        <Form.Control
                                                            type="number"
                                                            value={localChangeOrderDetails.find(d => d.ID === detail.ID)?.EngineeringLaborHours || ''}
                                                            onChange={(e) => handleInputChange(detail.ID, 'EngineeringLaborHours', e.target.value)}
                                                            onBlur={(e) => handleInputBlur(detail.ID, 'EngineeringLaborHours', e.target.value)}
                                                        />
                                                        <div className="d-flex align-items-center mt-2">
                                                            <Form.Check
                                                                type="switch"
                                                                checked={localChangeOrderDetails.find(d => d.ID === detail.ID)?.EngineeringCostCredit === true || localChangeOrderDetails.find(d => d.ID === detail.ID)?.EngineeringCostCredit === "true"}
                                                                onChange={(e) => handleToggleChange(detail.ID, 'EngineeringCostCredit', e.target.checked)}
                                                                className="me-2"
                                                            />
                                                            <Form.Label className="mb-0">
                                                                {(localChangeOrderDetails.find(d => d.ID === detail.ID)?.EngineeringCostCredit === true || localChangeOrderDetails.find(d => d.ID === detail.ID)?.EngineeringCostCredit === "true") ? 'Credit' : 'Cost'}
                                                            </Form.Label>
                                                        </div>
                                                    </Form.Group>
                                                </Col>
                                            </Row>
                                            <Row>
                                                <Col>
                                                    <Form.Group controlId={`materialsVariance-w-${index}`}>
                                                        <Form.Label>Materials Variance ($)</Form.Label>
                                                        <Form.Control
                                                            type="number"
                                                            value={localChangeOrderDetails.find(d => d.ID === detail.ID)?.MaterialsVariance || ''}
                                                            onChange={(e) => handleInputChange(detail.ID, 'MaterialsVariance', e.target.value)}
                                                            onBlur={(e) => handleInputBlur(detail.ID, 'MaterialsVariance', e.target.value)}
                                                        />
                                                        <div className="d-flex align-items-center mt-2">
                                                            <Form.Check
                                                                type="switch"
                                                                checked={localChangeOrderDetails.find(d => d.ID === detail.ID)?.MaterialCostCredit === true || localChangeOrderDetails.find(d => d.ID === detail.ID)?.MaterialCostCredit === "true"}
                                                                onChange={(e) => handleToggleChange(detail.ID, 'MaterialCostCredit', e.target.checked)}
                                                                className="me-2"
                                                            />
                                                            <Form.Label className="mb-0">
                                                                {(localChangeOrderDetails.find(d => d.ID === detail.ID)?.MaterialCostCredit === true || localChangeOrderDetails.find(d => d.ID === detail.ID)?.MaterialCostCredit === "true") ? 'Credit' : 'Cost'}
                                                            </Form.Label>
                                                        </div>
                                                    </Form.Group>
                                                </Col>
                                                <Col>
                                                    <Form.Group controlId={`scrap-w-${index}`}>
                                                        <Form.Label>Scrap ($)</Form.Label>
                                                        <Form.Control
                                                            type="number"
                                                            value={localChangeOrderDetails.find(d => d.ID === detail.ID)?.Scrap || ''}
                                                            onChange={(e) => handleInputChange(detail.ID, 'Scrap', e.target.value)}
                                                            onBlur={(e) => handleInputBlur(detail.ID, 'Scrap', e.target.value)}
                                                        />
                                                        <div className="d-flex align-items-center mt-2">
                                                            <Form.Check
                                                                type="switch"
                                                                checked={localChangeOrderDetails.find(d => d.ID === detail.ID)?.ScrapCostCredit === true || localChangeOrderDetails.find(d => d.ID === detail.ID)?.ScrapCostCredit === "true"}
                                                                onChange={(e) => handleToggleChange(detail.ID, 'ScrapCostCredit', e.target.checked)}
                                                                className="me-2"
                                                            />
                                                            <Form.Label className="mb-0">
                                                                {(localChangeOrderDetails.find(d => d.ID === detail.ID)?.ScrapCostCredit === true || localChangeOrderDetails.find(d => d.ID === detail.ID)?.ScrapCostCredit === "true") ? 'Credit' : 'Cost'}
                                                            </Form.Label>
                                                        </div>
                                                    </Form.Group>
                                                </Col>
                                            </Row>
                                        </Form>
                                        {formatCostImpactMessage(localChangeOrderDetails.find(d => d.ID === detail.ID), "Watertronics") && (
                                            <div className="mt-3 p-2" style={{backgroundColor: '#fff3cd', borderLeft: '4px solid #ffc107'}}>
                                                <strong>{formatCostImpactMessage(localChangeOrderDetails.find(d => d.ID === detail.ID), "Watertronics")}</strong>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                {followUp.length > 0 && (
                        <div>
                            <h2 className="mt-3">Follow Up</h2>
                            <div className="border p-1 mb-3">
                                {followUp.map((detail, index) => (
                                    <div key={index} className="border p-2 mb-2">
                                        <p className="mt-2 mb-2">{getChangeLabel(detail)} changed from {getDetailExistingValue(detail)} to {getDetailNewValue(detail)}</p>

                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {filteredTbdList.length > 0 && (
                        <div>
                            <h2 className="mt-3">TBD List</h2>
                            <div className="border p-1 mb-3">
                                {filteredTbdList.map((tbdItem, index) => (
                                    <ul key={index}>
                                        <li>
                                            <p className="mt-2 mb-2"><strong>{tbdItem.ColumnName || "BLANK"} ({tbdItem.Table || "BLANK"}):</strong> {tbdItem.Value || "BLANK"}</p>
                                        </li>
                                    </ul>
                                ))}
                            </div>
                        </div>
                    )}
                    <hr />
                    <div>
                        <h2 className="mt-3">Cost To/ Credit To</h2>
                        {(totalManufacturingLabor !== 0 || totalEngineeringLabor !== 0 || totalMaterialsVariance !== 0 || totalScrap !== 0 || customerBillTotal !== 0 || watertronicsBillTotal !== 0) ? (
                            <>
                                {totalManufacturingLabor !== 0 && (
                                    <p><strong>Manufacturing Labor Total ($90/hr):</strong> ${formatCostCredit(totalManufacturingLabor)}</p>
                                )}
                                {totalEngineeringLabor !== 0 && (
                                    <p><strong>Engineering Labor Total ($165/hr):</strong> ${formatCostCredit(totalEngineeringLabor)}</p>
                                )}
                                {totalMaterialsVariance !== 0 && (
                                    <p><strong>Materials Variance Total:</strong> ${formatCostCredit(totalMaterialsVariance)}</p>
                                )}
                                {totalScrap !== 0 && (
                                    <p><strong>Scrap Total:</strong> ${formatCostCredit(totalScrap)}</p>
                                )}
                                <p><strong>{customerBillTotal > 0 ? 'Credit to Customer:' : 'Charge to Customer:'}</strong> {customerBillTotal !== 0 ? `$${Math.abs(customerBillTotal).toFixed(2)}` : 'None'}</p>
                                {watertronicsBillTotal !== 0 && (
                                    <p><strong>Watertronics Bill Total:</strong> ${formatCostCredit(watertronicsBillTotal)}</p>
                                )}
                            </>
                        ) : (
                            <>
                                <p><strong>Charge to Customer:</strong> None</p>
                                <p>No other cost or credit information to display.</p>
                            </>
                        )}
                    </div>
                    <hr />
                    <Form.Group controlId="notes" className="mt-3">
                        <Form.Label><strong>Notes:</strong></Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={notes}
                            onChange={handleNotesChange}
                        />
                    </Form.Group>
                </>
            ) : (
                <p>Loading project details...</p>
            )}
        </Container>
    );
};

export default EditChangeOrder;
